import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';
import { useModal } from '../modalContext';
import { FacebookVerification } from './FacebookVerification';
import { Failed } from './Failed';
import { StripeInProgress } from './StripeInProgress';
import { StripeSecondaryVerification } from './StripeSecondaryVerification';
import { VerificationHeader } from './VerificationHeader';
import { VerificationProcessing } from './VerificationProcessing';
import { Verified } from './Verified';

export const IdentityVerificationModalContent = observer((): JSX.Element => {
  const { stores } = useDependencies();
  const modal = useModal();
  const { t } = useTranslation();
  const { verification } = stores;
  const [showWhy, setShowWhy] = useState<boolean>(false);

  useEffect(() => {
    verification.getStripeVerification();
  }, []);

  const clickShowWhy = (): void => {
    setShowWhy(!showWhy);
  }

  const closeModal = (): void => {
    modal?.setModalName(undefined);
  }

  const isProcessing = (): boolean => verification.stripeProcessing() || verification.loadingFacebook;

  return (
    <div>
      <Button aria-label='Close modal' className='float-right' onClick={closeModal}>
        <FontAwesomeIcon icon={faXmark} />
      </Button>
      {verification.isVerified() && (<Verified />)}
      {verification.isFailed() && <Failed />}
      {!verification.isVerified() && isProcessing() && (<VerificationProcessing />)}
      {!verification.isVerified() && !verification.isFailed() && !isProcessing() &&
        (
          <>
            <VerificationHeader />
            <div className='flex mt-6 md:mt-11 md:space-x-11 space-y-6 md:space-y-0 justify-center flex-col md:flex-row'>
              {!verification.stripeComplete() && verification.stripeInProgress() && verification.canStripeVerify()
                && <StripeInProgress />}
              {verification.canFacebookVerify() && !verification.loadingFacebook && <FacebookVerification />}
              {verification.canStripeVerify() && !verification.isVerified()
                && !verification.stripeInProgress() && <StripeSecondaryVerification />}
            </div>
          </>
        )}
      <hr className='mt-6 mb-6' />
      <Button
        buttonType={ButtonType.tertiary}
        className='text-sapphire font-bold mb-2'
        onClick={clickShowWhy}>
        {t('identityCheck.why')} {!showWhy && (`+ ${t('identityCheck.expand')}`)}
      </Button>
      {showWhy && (
        <div className='text-sm'>
          {t('identityCheck.whyDescription')}{' '}
          <Button
            buttonType={ButtonType.tertiary}
            className='text-sapphire font-bold'
            onClick={clickShowWhy}>
            - {t('identityCheck.collapse')}
          </Button>
        </div>
      )}
    </div>
  );
});
